import React, { useCallback, useEffect, useState } from 'react';
import {
  Users,
  UserPlus,
  Search,
  Shield,
  ShieldOff,
  CheckCircle,
  XCircle,
  MailCheck,
  MailX,
  Loader2,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  AlertTriangle,
  Coins,
  KeyRound,
  X,
} from 'lucide-react';
import { apiClient, AdminUserView, AdminUserUsageView } from '@/utils/apiClient';
import { useAuthStore } from '@/stores/authStore';
import { useTranslation, formatCount } from '@/i18n';
import { clsx } from 'clsx';

// ─── Confirmation Dialog ───

const ConfirmDialog: React.FC<{
  open: boolean;
  title: string;
  message: string;
  confirmLabel: string;
  danger?: boolean;
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}> = ({ open, title, message, confirmLabel, danger, loading, onConfirm, onCancel }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onCancel}>
      <div
        className="w-full max-w-sm mx-4 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-xl p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 mb-4">
          <AlertTriangle className={clsx('h-5 w-5 mt-0.5 flex-shrink-0', danger ? 'text-red-500' : 'text-amber-500')} />
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white">{title}</h3>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{message}</p>
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <button
            onClick={onCancel}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            {((useTranslation as any)().t as any)?.common?.cancel || 'Cancel'}
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className={clsx(
              'px-4 py-2 text-sm font-medium rounded-lg text-white transition-colors disabled:opacity-50',
              danger
                ? 'bg-red-600 hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600'
                : 'bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600'
            )}
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Role Badge (clickable for admins) ───

const ROLES = ['admin', 'user', 'viewer'] as const;

const roleLabel = (role: string, t: ReturnType<typeof useTranslation>['t']): string => {
  if (role === 'admin') return t.admin.roleAdmin;
  if (role === 'viewer') return t.admin.roleViewer;
  return t.admin.roleUser;
};

const RoleBadge: React.FC<{
  userId: number;
  role: string;
  isSelf: boolean;
  onChanged: () => void;
}> = ({ userId, role, isSelf, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();

  const handleChange = async (newRole: string) => {
    if (newRole === role) { setOpen(false); return; }
    setLoading(true);
    try {
      await apiClient.adminChangeRole(userId, newRole);
      onChanged();
    } catch { /* error handled by parent */ }
    setLoading(false);
    setOpen(false);
  };

  const colorClass =
    role === 'admin'
      ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300'
      : role === 'viewer'
      ? 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
      : 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300';

  if (isSelf) {
    return (
      <span className={clsx('inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium', colorClass)}>
        {roleLabel(role, t)}
      </span>
    );
  }

  return (
    <div className="relative inline-block">
      <button
        onClick={() => setOpen(!open)}
        disabled={loading}
        className={clsx(
          'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium cursor-pointer hover:ring-2 hover:ring-blue-300 transition-all',
          colorClass
        )}
        title={(t as any).admin?.changeRole || 'Change role'}
      >
        {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : roleLabel(role, t)}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute z-50 mt-1 left-0 min-w-[100px] rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg py-1">
            {ROLES.map((r) => (
              <button
                key={r}
                onClick={() => handleChange(r)}
                className={clsx(
                  'w-full text-left px-3 py-1.5 text-xs hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors',
                  r === role ? 'font-bold text-blue-600 dark:text-blue-400' : 'text-gray-700 dark:text-gray-300'
                )}
              >
                {roleLabel(r, t)}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// ─── Token formatting ───

const fmtTokens = (n: number | null | undefined): string => {
  if (n === null || n === undefined) return '∞';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(n % 1_000 === 0 ? 0 : 1)}K`;
  return String(n);
};

// ─── Create User Modal (STORY-122) ───

const inputClass =
  'w-full px-3 py-2 rounded-lg border text-sm border-gray-300 dark:border-gray-600 ' +
  'bg-white dark:bg-gray-700 text-gray-900 dark:text-white ' +
  'focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors';

const CreateUserModal: React.FC<{
  open: boolean;
  onClose: () => void;
  onCreated: (message: string) => void;
}> = ({ open, onClose, onCreated }) => {
  const { t } = useTranslation();
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('user');
  const [budget, setBudget] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setUsername(''); setEmail(''); setPassword(''); setRole('user'); setBudget('');
      setError(null);
    }
  }, [open]);

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const created = await apiClient.adminCreateUser({
        username,
        email,
        password,
        role,
        token_budget_monthly: budget.trim() === '' ? null : Number(budget),
      });
      onCreated(`${(t as any).admin?.userCreated || 'User created'}: ${created.username}`);
      onClose();
    } catch (err: any) {
      setError(err?.message || (t as any).admin?.actionFailed || 'Action failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-md mx-4 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-xl p-6 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-blue-500" />
            {(t as any).admin?.createUserTitle || 'Create user'}
          </h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <X className="h-4 w-4" />
          </button>
        </div>

        {error && (
          <div className="px-3 py-2 rounded-lg bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">
            {error}
          </div>
        )}

        <input
          type="text"
          required
          minLength={3}
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          placeholder={t.auth.username}
          className={inputClass}
        />
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t.auth.email}
          className={inputClass}
        />
        <input
          type="password"
          required
          minLength={6}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder={t.auth.password}
          autoComplete="new-password"
          className={inputClass}
        />
        <div className="flex gap-3">
          <select value={role} onChange={(e) => setRole(e.target.value)} className={inputClass}>
            {ROLES.map((r) => (
              <option key={r} value={r}>{roleLabel(r, t)}</option>
            ))}
          </select>
          <input
            type="number"
            min={0}
            value={budget}
            onChange={(e) => setBudget(e.target.value)}
            placeholder={(t as any).admin?.budgetPlaceholder || 'Token budget (empty = unlimited)'}
            className={inputClass}
          />
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            {(t as any).common?.cancel || 'Cancel'}
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 transition-colors disabled:opacity-50"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : ((t as any).admin?.create || 'Create')}
          </button>
        </div>
      </form>
    </div>
  );
};

// ─── Budget Modal (STORY-121) ───

const BudgetModal: React.FC<{
  user: AdminUserView | null;
  usage?: AdminUserUsageView;
  onClose: () => void;
  onSaved: (message: string) => void;
}> = ({ user, usage, onClose, onSaved }) => {
  const { t } = useTranslation();
  const [budget, setBudget] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      setBudget(user.token_budget_monthly === null ? '' : String(user.token_budget_monthly));
      setError(null);
    }
  }, [user]);

  if (!user) return null;

  const save = async (value: number | null) => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.adminSetBudget(user.id, value);
      onSaved(res.message);
      onClose();
    } catch (err: any) {
      setError(err?.message || (t as any).admin?.actionFailed || 'Action failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div
        className="w-full max-w-sm mx-4 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-xl p-6 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <Coins className="h-5 w-5 text-amber-500" />
            {(t as any).admin?.budgetTitle || 'Monthly token budget'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <X className="h-4 w-4" />
          </button>
        </div>

        <p className="text-sm text-gray-600 dark:text-gray-400">
          {user.username} ({user.email})
          {usage && (
            <span className="block mt-1 text-xs">
              {(t as any).admin?.usedThisMonth || 'Used this month'}: {fmtTokens(usage.total_tokens)}
            </span>
          )}
        </p>

        {error && (
          <div className="px-3 py-2 rounded-lg bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">
            {error}
          </div>
        )}

        <input
          type="number"
          min={0}
          value={budget}
          onChange={(e) => setBudget(e.target.value)}
          placeholder={(t as any).admin?.budgetPlaceholder || 'Token budget (empty = unlimited)'}
          className={inputClass}
        />

        <div className="flex justify-end gap-2">
          <button
            onClick={() => save(null)}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            {(t as any).admin?.budgetUnlimited || 'Unlimited'}
          </button>
          <button
            onClick={() => save(budget.trim() === '' ? null : Number(budget))}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 transition-colors disabled:opacity-50"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : ((t as any).common?.save || 'Save')}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Password Reset Modal (STORY-122 AC-3) ───

const PasswordModal: React.FC<{
  user: AdminUserView | null;
  onClose: () => void;
  onSaved: (message: string) => void;
}> = ({ user, onClose, onSaved }) => {
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user) { setPassword(''); setError(null); }
  }, [user]);

  if (!user) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.adminResetPassword(user.id, password);
      onSaved(res.message);
      onClose();
    } catch (err: any) {
      setError(err?.message || (t as any).admin?.actionFailed || 'Action failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-sm mx-4 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-xl p-6 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-blue-500" />
            {(t as any).admin?.resetPasswordTitle || 'Reset password'}
          </h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <X className="h-4 w-4" />
          </button>
        </div>

        <p className="text-sm text-gray-600 dark:text-gray-400">
          {user.username} ({user.email})
        </p>

        {error && (
          <div className="px-3 py-2 rounded-lg bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">
            {error}
          </div>
        )}

        <input
          type="password"
          required
          minLength={6}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder={(t as any).admin?.newPassword || 'New password'}
          autoComplete="new-password"
          className={inputClass}
        />

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            {(t as any).common?.cancel || 'Cancel'}
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 transition-colors disabled:opacity-50"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : ((t as any).common?.save || 'Save')}
          </button>
        </div>
      </form>
    </div>
  );
};

// ─── Main Component ───

export const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<AdminUserView[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [budgetUser, setBudgetUser] = useState<AdminUserView | null>(null);
  const [passwordUser, setPasswordUser] = useState<AdminUserView | null>(null);
  const [usageByUser, setUsageByUser] = useState<Map<number, AdminUserUsageView>>(new Map());

  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
    confirmLabel: string;
    danger: boolean;
    action: (() => Promise<void>) | null;
  }>({ open: false, title: '', message: '', confirmLabel: '', danger: false, action: null });

  const { t, language } = useTranslation();
  const { user: currentUser } = useAuthStore();
  const totalPages = Math.ceil(total / pageSize);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.adminListUsers({
        page,
        page_size: pageSize,
        search: search || undefined,
      });
      setUsers(res.users);
      setTotal(res.total);
      // Month-to-date usage — best-effort, table renders without it
      try {
        const usage = await apiClient.adminGetUsersUsage();
        setUsageByUser(new Map(
          usage.users
            .filter((u): u is AdminUserUsageView & { user_id: number } => u.user_id !== null)
            .map((u) => [u.user_id, u])
        ));
      } catch { /* usage endpoint unavailable — skip */ }
    } catch (err: any) {
      setError(err?.message || (t as any).admin?.loadFailed || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, t]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => { setPage(1); }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const confirmAction = (title: string, message: string, confirmLabel: string, danger: boolean, action: () => Promise<void>) => {
    setConfirmDialog({ open: true, title, message, confirmLabel, danger, action });
  };

  const executeConfirmedAction = async () => {
    if (!confirmDialog.action) return;
    setActionLoading(-1);
    try {
      await confirmDialog.action();
      await fetchUsers();
    } catch (err: any) {
      setError(err?.message || (t as any).admin?.actionFailed || 'Action failed');
    } finally {
      setActionLoading(null);
      setConfirmDialog((d) => ({ ...d, open: false }));
    }
  };

  const handleToggleActive = (user: AdminUserView) => {
    if (user.is_active) {
      confirmAction(
        (t as any).admin?.deactivateUser || 'Deactivate user',
        t.admin.deactivateConfirm
          .replace('{username}', user.username)
          .replace('{email}', user.email),
        (t as any).admin?.deactivate || 'Deactivate',
        true,
        async () => {
          const res = await apiClient.adminDeactivateUser(user.id);
          setSuccessMsg(res.message);
        }
      );
    } else {
      confirmAction(
        (t as any).admin?.activateUser || 'Activate user',
        t.admin.activateConfirm
          .replace('{username}', user.username)
          .replace('{email}', user.email),
        (t as any).admin?.activate || 'Activate',
        false,
        async () => {
          const res = await apiClient.adminActivateUser(user.id);
          setSuccessMsg(res.message);
        }
      );
    }
  };

  const handleVerifyEmail = (user: AdminUserView) => {
    confirmAction(
      (t as any).admin?.verifyEmailTitle || 'Verify email',
      t.admin.verifyEmailConfirm
        .replace('{username}', user.username)
        .replace('{email}', user.email),
      (t as any).admin?.verify || 'Verify',
      false,
      async () => {
        const res = await apiClient.adminVerifyEmail(user.id);
        setSuccessMsg(res.message);
      }
    );
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  };

  // Auto-dismiss success message
  useEffect(() => {
    if (!successMsg) return;
    const timer = setTimeout(() => setSuccessMsg(null), 4000);
    return () => clearTimeout(timer);
  }, [successMsg]);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className="h-5 w-5 text-gray-600 dark:text-gray-400" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {(t as any).admin?.userManagement || 'User Management'}
          </h3>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            ({formatCount(language, total, t.admin.totalUsers)})
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setCreateOpen(true)}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 transition-colors"
          >
            <UserPlus className="h-4 w-4" />
            {(t as any).admin?.createUser || 'Create user'}
          </button>
          <button
            onClick={fetchUsers}
            disabled={loading}
            className="p-2 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <RefreshCw className={clsx('h-4 w-4', loading && 'animate-spin')} />
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={(t as any).admin?.searchPlaceholder || 'Search…'}
          className={clsx(
            'w-full pl-9 pr-3 py-2 rounded-lg border text-sm',
            'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700',
            'text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors'
          )}
        />
      </div>

      {/* Messages */}
      {error && (
        <div className="px-4 py-3 rounded-lg bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}
      {successMsg && (
        <div className="px-4 py-3 rounded-lg bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 text-sm text-green-700 dark:text-green-300">
          {successMsg}
        </div>
      )}

      {/* Table */}
      {loading && users.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 text-blue-500 animate-spin" />
        </div>
      ) : users.length === 0 ? (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          {(t as any).admin?.noUsers || 'No users found'}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400">
              <tr>
                <th className="px-4 py-3 text-left font-medium">ID</th>
                <th className="px-4 py-3 text-left font-medium">{t.auth.username}</th>
                <th className="px-4 py-3 text-left font-medium">{t.auth.email}</th>
                <th className="px-4 py-3 text-center font-medium">{(t as any).admin?.role || 'Role'}</th>
                <th className="px-4 py-3 text-center font-medium">{(t as any).admin?.emailStatus || 'Email'}</th>
                <th className="px-4 py-3 text-center font-medium">{(t as any).admin?.status || 'Status'}</th>
                <th className="px-4 py-3 text-center font-medium">{(t as any).admin?.budget || 'Budget'}</th>
                <th className="px-4 py-3 text-left font-medium">{(t as any).admin?.lastLogin || 'Last Login'}</th>
                <th className="px-4 py-3 text-center font-medium">{(t as any).admin?.actions || 'Actions'}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {users.map((user) => {
                const isSelf = user.id === currentUser?.id;
                return (
                  <tr
                    key={user.id}
                    className={clsx(
                      'bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors',
                      !user.is_active && 'opacity-60'
                    )}
                  >
                    <td className="px-4 py-3 text-gray-500 dark:text-gray-400">{user.id}</td>
                    <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">
                      {user.username}
                      {isSelf && <span className="ml-1.5 text-xs text-gray-400">({t.admin.you})</span>}
                    </td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{user.email}</td>
                    <td className="px-4 py-3 text-center">
                      <RoleBadge userId={user.id} role={user.role} isSelf={isSelf} onChanged={fetchUsers} />
                    </td>
                    <td className="px-4 py-3 text-center">
                      {user.email_verified ? (
                        <span className="inline-block" title={(t as any).admin?.emailVerified || 'Verified'}>
                          <MailCheck className="h-4 w-4 text-green-500 mx-auto" />
                        </span>
                      ) : (
                        <button
                          onClick={() => handleVerifyEmail(user)}
                          className="mx-auto flex items-center"
                          title={(t as any).admin?.clickToVerify || 'Click to manually verify'}
                        >
                          <MailX className="h-4 w-4 text-amber-500 hover:text-amber-600 transition-colors" />
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {user.is_active ? (
                        <span className="inline-block" title={(t as any).admin?.active || 'Active'}>
                          <CheckCircle className="h-4 w-4 text-green-500 mx-auto" />
                        </span>
                      ) : (
                        <span className="inline-block" title={(t as any).admin?.deactivated || 'Deactivated'}>
                          <XCircle className="h-4 w-4 text-red-500 mx-auto" />
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => setBudgetUser(user)}
                        className={clsx(
                          'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium cursor-pointer hover:ring-2 hover:ring-amber-300 transition-all',
                          user.token_budget_monthly !== null
                            ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                        )}
                        title={(t as any).admin?.budgetTitle || 'Monthly token budget'}
                      >
                        <Coins className="h-3 w-3" />
                        {usageByUser.has(user.id)
                          ? `${fmtTokens(usageByUser.get(user.id)!.total_tokens)} / ${fmtTokens(user.token_budget_monthly)}`
                          : fmtTokens(user.token_budget_monthly)}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-gray-500 dark:text-gray-400 text-xs">
                      {formatDate(user.last_login)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {!isSelf && (
                        <button
                          onClick={() => setPasswordUser(user)}
                          className="inline-flex items-center gap-1 px-2 py-1.5 mr-1.5 rounded-md text-xs font-medium text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-600 transition-colors"
                          title={(t as any).admin?.resetPasswordTitle || 'Reset password'}
                        >
                          <KeyRound className="h-3 w-3" />
                        </button>
                      )}
                      {!isSelf && (
                        <button
                          onClick={() => handleToggleActive(user)}
                          disabled={actionLoading === user.id}
                          className={clsx(
                            'inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium transition-colors',
                            'disabled:opacity-50 disabled:cursor-not-allowed',
                            user.is_active
                              ? 'text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 hover:bg-red-100 dark:hover:bg-red-900/50 border border-red-200 dark:border-red-800'
                              : 'text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/30 hover:bg-green-100 dark:hover:bg-green-900/50 border border-green-200 dark:border-green-800'
                          )}
                        >
                          {actionLoading === user.id ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : user.is_active ? (
                            <>
                              <ShieldOff className="h-3 w-3" />
                              {(t as any).admin?.deactivate || 'Deactivate'}
                            </>
                          ) : (
                            <>
                              <Shield className="h-3 w-3" />
                              {(t as any).admin?.activate || 'Activate'}
                            </>
                          )}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {(t as any).admin?.page || 'Page'} {page} / {totalPages}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="p-1.5 rounded-md border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-30 transition-colors"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="p-1.5 rounded-md border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-30 transition-colors"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmLabel={confirmDialog.confirmLabel}
        danger={confirmDialog.danger}
        loading={actionLoading === -1}
        onConfirm={executeConfirmedAction}
        onCancel={() => setConfirmDialog((d) => ({ ...d, open: false }))}
      />

      {/* Create User Modal (STORY-122) */}
      <CreateUserModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(msg) => { setSuccessMsg(msg); fetchUsers(); }}
      />

      {/* Budget Modal (STORY-121) */}
      <BudgetModal
        user={budgetUser}
        usage={budgetUser ? usageByUser.get(budgetUser.id) : undefined}
        onClose={() => setBudgetUser(null)}
        onSaved={(msg) => { setSuccessMsg(msg); fetchUsers(); }}
      />

      {/* Password Reset Modal (STORY-122 AC-3) */}
      <PasswordModal
        user={passwordUser}
        onClose={() => setPasswordUser(null)}
        onSaved={(msg) => { setSuccessMsg(msg); }}
      />
    </div>
  );
};
